import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Clock, Layers, TrendingUp, TrendingDown, Timer, Users, BarChart3 } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import api, { formatApiError } from '../../api';
import PageHeader from '../../components/PageHeader';

const STATUS_LABELS = {
    submitted: 'New', awaiting_information: 'Awaiting Info', awaiting_client_approval: 'Awaiting Approval',
    under_review: 'Under Review', approved: 'Approved', backlog: 'Backlog', planned: 'Planned',
    in_progress: 'In Progress', awaiting_client_validation: 'Awaiting Validation', completed: 'Completed',
    closed: 'Closed', declined: 'Declined',
};

function monthDelta(current, previous) {
    if (previous === 0) return current > 0 ? { text: `+${current} vs last month`, up: true } : null;
    const diff = current - previous;
    const pct = Math.round((diff / previous) * 100);
    return { text: `${diff >= 0 ? '+' : ''}${pct}% vs last month`, up: diff >= 0 };
}

function formatDuration(hours) {
    if (hours == null) return '—';
    if (hours < 48) return `${hours.toFixed(1)}h`;
    return `${(hours / 24).toFixed(1)}d`;
}

function StatCard({ label, value, testId, tone, delta }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-4" data-testid={testId}>
            <div className="text-[11px] text-[color:var(--bm-text-subtle)] uppercase tracking-wide font-semibold">{label}</div>
            <div className={`text-[26px] font-display font-bold mt-1 ${tone || 'text-[color:var(--bm-text)]'}`}>{value}</div>
            {delta && (
                <div className={`flex items-center gap-1 text-[11px] font-medium mt-1 ${delta.up ? 'text-emerald-600' : 'text-rose-600'}`} data-testid={`${testId}-delta`}>
                    {delta.up ? <TrendingUp size={11} /> : <TrendingDown size={11} />} {delta.text}
                </div>
            )}
        </div>
    );
}

function BreakdownCard({ title, data, testId }) {
    const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={testId}>
            <div className="font-display text-[14px] font-semibold mb-3">{title}</div>
            {entries.length === 0 ? <div className="text-[12px] text-[color:var(--bm-text-subtle)]">No open requests</div> : (
                <div className="space-y-2">
                    {entries.map(([k, v]) => (
                        <div key={k} className="flex items-center justify-between text-[12.5px]">
                            <span className="text-[color:var(--bm-text-muted)] truncate">{STATUS_LABELS[k] || k}</span>
                            <span className="font-semibold text-[color:var(--bm-text)]">{v}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TrendChart({ data, testId }) {
    const rows = (data || []).map(d => ({ ...d, label: d.date.slice(5) }));
    const hubTotal = rows.reduce((s, r) => s + (r.hub || 0), 0);
    const manualTotal = rows.reduce((s, r) => s + (r.manual || 0), 0);
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={testId}>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 font-display text-[14px] font-semibold"><TrendingUp size={15} /> Manual vs Hub — last 30 days</div>
                <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">
                    <span className="font-semibold text-[color:var(--bm-primary)]">{hubTotal} Hub</span> · <span className="font-semibold text-violet-600">{manualTotal} Manual</span>
                </div>
            </div>
            <div className="text-[11.5px] text-[color:var(--bm-text-subtle)] mb-3">Are clients adopting the Hub, or still leaning on WhatsApp/manual logging?</div>
            <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                    <AreaChart data={rows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="hubGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#0F5F6B" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#0F5F6B" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="manualGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.35} />
                                <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                        <XAxis dataKey="label" tick={{ fontSize: 10.5 }} interval={4} />
                        <YAxis tick={{ fontSize: 10.5 }} allowDecimals={false} width={28} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={(l) => `Date: ${l}`} />
                        <Legend wrapperStyle={{ fontSize: 11.5 }} />
                        <Area type="monotone" dataKey="hub" name="Hub" stroke="#0F5F6B" fill="url(#hubGrad)" strokeWidth={2} />
                        <Area type="monotone" dataKey="manual" name="Manual" stroke="#7c3aed" fill="url(#manualGrad)" strokeWidth={2} />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function TopClientsCard({ items, testId }) {
    const max = Math.max(1, ...(items || []).map(i => i.count));
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 font-display text-[14px] font-semibold mb-3"><Users size={15} /> Who's logging the most requests</div>
            {(!items || items.length === 0) ? <div className="text-[12px] text-[color:var(--bm-text-subtle)]">No requests yet</div> : (
                <div className="space-y-2.5">
                    {items.map(c => (
                        <div key={c.client_name} className="text-[12.5px]" data-testid={`dash-top-client-${c.client_name}`}>
                            <div className="flex items-center justify-between mb-0.5">
                                <span className="text-[color:var(--bm-text-muted)] truncate">{c.client_name}</span>
                                <span className="font-semibold text-[color:var(--bm-text)]">{c.count}</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${(c.count / max) * 100}%`, background: 'var(--bm-primary)' }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ReceivedVsClosedChart({ data, testId }) {
    const rows = (data || []).map(d => ({ ...d, label: d.date.slice(5) }));
    const totalReceived = rows.reduce((s, r) => s + (r.received || 0), 0);
    const totalClosed = rows.reduce((s, r) => s + (r.closed || 0), 0);
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid={testId}>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 font-display text-[14px] font-semibold"><BarChart3 size={15} /> Received vs Closed — last 30 days</div>
                <div className="text-[11.5px] text-[color:var(--bm-text-subtle)]">
                    <span className="font-semibold text-slate-700">{totalReceived} received</span> · <span className="font-semibold text-emerald-600">{totalClosed} closed</span>
                </div>
            </div>
            <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                    <BarChart data={rows} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f4" />
                        <XAxis dataKey="label" tick={{ fontSize: 10.5 }} interval={4} />
                        <YAxis tick={{ fontSize: 10.5 }} allowDecimals={false} width={28} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} labelFormatter={(l) => `Date: ${l}`} />
                        <Legend wrapperStyle={{ fontSize: 11.5 }} />
                        <Bar dataKey="received" name="Received" fill="#64748b" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="closed" name="Closed" fill="#10b981" radius={[3, 3, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default function SupportDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get('/support/dashboard');
            setData(r.data);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    if (!data) return <div className="p-8 text-[color:var(--bm-text-muted)]">Loading…</div>;

    return (
        <div data-testid="support-dashboard-page" className="bm-fade-in">
            <PageHeader
                title="Support Hub — Dashboard"
                description="Open request volumes across all platforms and clients."
                actions={
                    <button onClick={() => navigate('/support-hub')} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[color:var(--bm-primary)] hover:underline" data-testid="support-dashboard-back-btn">
                        <ArrowLeft size={14} /> Back to Kanban
                    </button>
                }
            />
            <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-5">
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                    <StatCard label="Open requests" value={data.open_total} testId="dash-open-total" />
                    <StatCard label="Submitted this month" value={data.submitted_this_month} testId="dash-this-month" delta={monthDelta(data.submitted_this_month, data.submitted_last_month)} />
                    <StatCard label="Avg time to close" value={formatDuration(data.avg_close_hours)} testId="dash-avg-close-time" />
                    <StatCard label="Awaiting approval" value={data.awaiting_approval} testId="dash-awaiting-approval" />
                    <StatCard label="Awaiting info" value={data.awaiting_information} testId="dash-awaiting-info" />
                    <StatCard label="Awaiting validation" value={data.awaiting_validation} testId="dash-awaiting-validation" />
                    <StatCard label="Overdue" value={data.overdue_count} tone={data.overdue_count > 0 ? 'text-rose-600' : ''} testId="dash-overdue" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <TrendChart data={data.submission_trend} testId="dash-submission-trend" />
                    <ReceivedVsClosedChart data={data.received_vs_closed} testId="dash-received-vs-closed" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <BreakdownCard title="Open requests by platform" data={data.by_platform} testId="dash-by-platform" />
                    <BreakdownCard title="Open requests by client" data={data.by_client} testId="dash-by-client" />
                    <TopClientsCard items={data.top_clients} testId="dash-top-clients" />
                    <BreakdownCard title="Issues vs improvements" data={data.by_type} testId="dash-by-type" />
                    <BreakdownCard title="By priority" data={data.by_priority} testId="dash-by-priority" />
                    <BreakdownCard title="By status" data={data.by_status} testId="dash-by-status" />
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid="dash-backlog-enhancements">
                        <div className="flex items-center gap-2 font-display text-[14px] font-semibold mb-1"><Layers size={15} /> Enhancements in backlog</div>
                        <div className="text-[26px] font-bold font-display mt-2">{data.backlog_enhancements}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-rose-200 p-5" data-testid="dash-overdue-list">
                        <div className="flex items-center gap-2 font-display text-[14px] font-semibold mb-3 text-rose-700"><AlertTriangle size={15} /> Overdue requests</div>
                        {data.overdue_items.length === 0 ? <div className="text-[12px] text-[color:var(--bm-text-subtle)]">None — nice work.</div> : (
                            <div className="space-y-2">
                                {data.overdue_items.map(t => (
                                    <div key={t.id} className="text-[12.5px] flex items-center justify-between">
                                        <span className="truncate">{t.ticket_number} · {t.title}</span>
                                        <span className="text-[color:var(--bm-text-subtle)]">{t.client_name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-5" data-testid="dash-oldest-open">
                        <div className="flex items-center gap-2 font-display text-[14px] font-semibold mb-3"><Clock size={15} /> Oldest open requests</div>
                        {data.oldest_open.length === 0 ? <div className="text-[12px] text-[color:var(--bm-text-subtle)]">No open requests</div> : (
                            <div className="space-y-2">
                                {data.oldest_open.map(t => (
                                    <div key={t.id} className="text-[12.5px] flex items-center justify-between">
                                        <span className="truncate">{t.ticket_number} · {t.title}</span>
                                        <span className="text-[color:var(--bm-text-subtle)]">{(t.created_at || '').slice(0, 10)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
